// Utilidades para el calendario de metales

/**
 * Formatea una fecha a string yyyy-mm-dd
 * @param {Date} date - Fecha a formatear
 * @returns {string} Fecha en formato yyyy-mm-dd
 */
export const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Construye una matriz de 6 semanas x 7 días para un mes dado
 * @param {number} year - Año
 * @param {number} month - Mes (0-11)
 * @returns {Date[][]} Matriz de fechas
 */
export const buildCalendarMatrix = (year, month) => {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  const day = first.getDay(); // 0=Domingo
  const offset = (day + 6) % 7; // Ajustar para que Lunes=0
  start.setDate(first.getDate() - offset);

  const matrix = [];
  for (let week = 0; week < 6; week++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      const cellDate = new Date(start);
      cellDate.setDate(start.getDate() + week * 7 + d);
      row.push(cellDate);
    }
    matrix.push(row);
  }
  return matrix;
};

/**
 * Normaliza los detalles de metales por fecha y ubicación
 * @param {Array} details - Array de registros de metales
 * @returns {Object} Mapa de necesidades por fecha
 */
export const normalizeDailyNeeds = (details) => {
  const map = {};
  if (!Array.isArray(details)) return map;
  
  for (const item of details) {
    const dateObj = item.fecha instanceof Date ? item.fecha : new Date(item.fecha);
    const key = toDateKey(dateObj);
    
    if (!map[key]) {
      map[key] = {
        cordoba: { gold: 0, platinum: 0 },
        euroshine: { gold: 0, platinum: 0 },
      };
    }
    
    const target = (item.location || "cordoba").toLowerCase();
    const loc = target.includes("euro") ? "euroshine" : "cordoba";
    map[key][loc].gold += parseFloat(item.goldRequired || 0);
    map[key][loc].platinum += parseFloat(item.platinumRequired || 0);
  }
  
  return map;
};

/**
 * Calcula la disponibilidad acumulada de metales día a día
 * @param {Date} cursor - Fecha de referencia del mes
 * @param {Object} baseStock - Stock inicial de metales
 * @param {Object} dailyNeeds - Necesidades diarias normalizadas
 * @returns {Object} Datos agregados con matriz y disponibilidad
 */
export const calculateDayAggregates = (cursor, baseStock, dailyNeeds) => {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const matrix = buildCalendarMatrix(year, month);

  let running = {
    cordoba: {
      gold: parseFloat(baseStock?.cordobaGold || 0),
      platinum: parseFloat(baseStock?.cordobaPlatinum || 0),
    },
    euroshine: {
      gold: parseFloat(baseStock?.euroshineGold || 0),
      platinum: parseFloat(baseStock?.euroshinePlatinum || 0),
    },
  };

  const result = {};
  const start = matrix[0][0];
  
  for (let i = 0; i < 42; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    const key = toDateKey(date);
    const needs = dailyNeeds[key] || { 
      cordoba: { gold: 0, platinum: 0 }, 
      euroshine: { gold: 0, platinum: 0 } 
    };

    const availableBefore = {
      cordoba: { ...running.cordoba },
      euroshine: { ...running.euroshine },
    };

    running.cordoba.gold -= needs.cordoba.gold;
    running.cordoba.platinum -= needs.cordoba.platinum;
    running.euroshine.gold -= needs.euroshine.gold;
    running.euroshine.platinum -= needs.euroshine.platinum;

    result[key] = {
      date,
      needs,
      availableBefore,
      availableAfter: {
        cordoba: { ...running.cordoba },
        euroshine: { ...running.euroshine },
      },
    };
  }
  
  return { matrix, data: result };
};

/**
 * Configuración de tamaños para el calendario
 */
export const CALENDAR_SIZES = {
  xs: { cell: 14, headerPx: 0.15, dot: 4 },
  sm: { cell: 24, headerPx: 0.25, dot: 6 },
  md: { cell: 34, headerPx: 0.5, dot: 6 },
};

/**
 * Calcula las dimensiones del calendario según el tamaño y configuración
 * @param {string} size - Tamaño del calendario
 * @param {boolean} compact - Si es vista compacta
 * @param {number} fixedHeight - Altura fija opcional
 * @param {boolean} showTitle - Si muestra título
 * @param {number} cellWidth - Ancho de celda opcional
 * @returns {Object} Dimensiones calculadas
 */
export const calculateDimensions = (size, compact, fixedHeight, showTitle, cellWidth) => {
  let dims = CALENDAR_SIZES[size] || CALENDAR_SIZES.md;
  const cellW = parseInt(cellWidth || dims.cell, 10);
  
  if (compact && fixedHeight) {
    const headerApprox = showTitle ? 44 : 28;
    const available = Math.max(fixedHeight - headerApprox, 60);
    const computedCell = Math.max(18, Math.floor(available / 6));
    dims = { 
      ...dims, 
      cell: computedCell, 
      dot: Math.min(6, Math.max(3, Math.floor(computedCell / 6))) 
    };
  }
  
  return { dims, cellW };
};

/**
 * Calcula la disponibilidad de metales día a día usando los datos de Excel
 * @param {Date} cursor - Fecha de referencia del mes
 * @param {number} stockTotalOro - Stock total de oro
 * @param {number} stockTotalPlatino - Stock total de platino
 * @param {Array} excelData - Datos de Excel con órdenes
 * @returns {Object} Datos agregados con matriz y disponibilidad por día
 */
export const calculateDailyAvailability = (cursor, stockTotalOro, stockTotalPlatino, excelData) => {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const matrix = buildCalendarMatrix(year, month);

  const excelSerialToDate = (serial) => {
    if (!serial || isNaN(serial)) return null;
    const excelEpoch = new Date('1899-12-30T00:00:00Z');
    const date = new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);
    return date;
  };

  const fechasFuturas = excelData.map(item => {
    return excelSerialToDate(item.fecha_entrega);
  }).filter(date => date && date > new Date());

  let fechaMasFutura = null;
  if (fechasFuturas.length > 0) {
    fechaMasFutura = new Date(Math.max(...fechasFuturas));
  }

  const METAL_PURITY_FACTORS = {
    GOLD: {
      '9kt': 0.3792,
      '14kt': 0.5875,
      '18kt': 0.752,
      '19kt': 0.8042
    },
    PLATINUM: 0.955,
    SILVER: 0.925
  };

  const result = {};
  const start = matrix[0][0];
  
  for (let i = 0; i < 42; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    const key = toDateKey(date);
    
    if (fechaMasFutura && date > fechaMasFutura) {
      result[key] = {
        date,
        oroDisponible: null,
        platinoDisponible: null,
        requeridoOro: 0,
        requeridoPlatino: 0,
        stockTotalOro,
        stockTotalPlatino,
      };
      continue;
    }
    
    const filteredOrders = excelData.filter(item => {
      const orderDate = excelSerialToDate(item.fecha_entrega);
      if (!orderDate) return false;
      
      const normalizedOrderDate = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());
      const normalizedCurrentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      return normalizedOrderDate <= normalizedCurrentDate;
    });

    
    let requeridoOro = 0;
    let requeridoPlatino = 0;
    
    filteredOrders.forEach(item => {
      const metal = item.metal || '';
      const gramos = parseFloat(item.gramos || 0);
      
      if (metal.toLowerCase().includes('plata')) {
        return;
      }

      let purityFactor = 0;
      if (metal.toLowerCase().includes('18kt') || metal.toLowerCase().includes('18 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['18kt'];
      } else if (metal.toLowerCase().includes('14kt') || metal.toLowerCase().includes('14 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['14kt'];
      } else if (metal.toLowerCase().includes('9kt') || metal.toLowerCase().includes('9 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['9kt'];
      } else if (metal.toLowerCase().includes('19kt') || metal.toLowerCase().includes('19 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['19kt'];
      } else if (metal.toLowerCase().includes('platino')) {
        purityFactor = METAL_PURITY_FACTORS.PLATINUM;
      }

      if (purityFactor === 0) return;

      const fino = gramos * purityFactor;

      if (metal.toLowerCase().includes('platino')) {
        requeridoPlatino += fino;
      } else {
        requeridoOro += fino;
      }
    });
    
    const oroDisponible = stockTotalOro - requeridoOro;
    const platinoDisponible = stockTotalPlatino - requeridoPlatino;

    result[key] = {
      date,
      oroDisponible,
      platinoDisponible,
      requeridoOro,
      requeridoPlatino,
      stockTotalOro,
      stockTotalPlatino,
    };
  }
  
  return { matrix, data: result };
};